import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Box,
} from '@mui/material';
import UserBadgesPopup from './Popups/UserBadgesPopup/UserBadgesPopup';
import ChangePasswordPopup from './Popups/UserAdminPopup/ChangePasswordPopup';
import Api from '../../utils/Api';
import { RoleIds } from '../../policies/Role';

// Page modifier par Frédérick BETHERMAT - 12-05-2026

/**
 * Le contenu et les actions disponibles varient selon le rôle de l'utilisateur connecté
 * (administrateur ou enseignant).
 */
class UserGrid extends React.Component {
  constructor(props) {
    super(props);

    // Référence vers la popup de gestion des badges pour appeler ses méthodes directement
    this.badgeAssignationPopup = React.createRef();

    this.state = {
      open: false, // Contrôle l'ouverture de la popup des badges
      openPasswordPopup: false, // Contrôle l'ouverture de la popup de changement de mot de passe
      confirmDelete: false, // Contrôle l'ouverture de la boîte de confirmation de suppression
      selectedUser: null, // Utilisateur actuellement sélectionné pour une action
      searchValue: null, // Valeur sélectionnée dans l'autocomplete
      inputValue: '', // Valeur saisie dans le champ de recherche
    };
  }

  /**
   * Ouvre la popup de gestion des badges pour l'utilisateur sélectionné
   */
  handleBadgeManagement = (user) => {
    this.setState({ open: true, selectedUser: user });
    this.badgeAssignationPopup.current.refreshBadgesAssignation(true);
  };

  /**
   * Ferme la popup de gestion des badges
   */
  handleClose = () => {
    this.setState({ open: false });
  };

  /**
   * Supprime l'utilisateur sélectionné via l'API
   */
  deleteUser = () => {
    const { selectedUser } = this.state;
    Api.delete(`/user/${selectedUser.id}`)
      .then(() => {
        this.props.refreshUsers?.();
        this.setState({ confirmDelete: false, selectedUser: null });
      })
      .catch(() => {
        alert('Erreur lors de la suppression.');
      });
  };

  /**
   * Active ou désactive un utilisateur via l'API
   */
  toggleUserStatus = (user) => {
    Api.post(`/user/${user.id}/toggle-active`)
      .then(() => {
        this.props.refreshAllUsers?.();
      })
      .catch(() => {
        alert('Erreur lors du changement de statut.');
      });
  };

  /**
   * Retourne la définition des colonnes pour la vue administrateur.
   * Inclut le rôle, le prénom, le nom, le statut et un menu d'actions.
   */
  getAdminColumns() {
    return [
      {
        field: 'role_id',
        headerName: 'Rôle',
        flex: 1,
        // Conversion de l'identifiant de rôle en libellé lisible
        valueGetter: (params) => {
          const roles = {
            1: 'Administrateur',
            2: 'Contact Admin',
            3: 'Professeur',
            4: 'Étudiant',
          };
          return roles[params] || 'Inconnu';
        },
      },
      { field: 'first_name', headerName: 'Prénom', flex: 1 },
      { field: 'last_name', headerName: 'Nom', flex: 1 },
      {
        field: 'active',
        headerName: 'Statut',
        flex: 1,
        // Conversion de la valeur numérique en libellé de statut
        valueGetter: (params) => (params === 1 ? 'Actif' : 'Désactivé'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 2,
        // Menu déroulant regroupant toutes les actions disponibles pour un utilisateur
        renderCell: (params) => (
          <Select value="" displayEmpty fullWidth renderValue={() => 'Actions'}>
            <MenuItem onClick={() => this.handleBadgeManagement(params.row)}>
              Gestion des badges
            </MenuItem>
            <MenuItem
              onClick={() =>
                this.setState({
                  openPasswordPopup: true,
                  selectedUser: params.row,
                })
              }
            >
              Modifier le mot de passe
            </MenuItem>
            <MenuItem
              onClick={() =>
                this.setState({ confirmDelete: true, selectedUser: params.row })
              }
            >
              Supprimer
            </MenuItem>
            {/* Libellé du bouton dynamique selon le statut actuel de l'utilisateur */}
            <MenuItem onClick={() => this.toggleUserStatus(params.row)}>
              {params.row.active === 1 ? 'Désactiver' : 'Activer'}
            </MenuItem>
          </Select>
        ),
      },
    ];
  }

  /**
   * Retourne la définition des colonnes pour la vue enseignant.
   * Affiche uniquement le prénom, le nom et un bouton de retrait de badge.
   */
  getTeacherColumns() {
    return [
      { field: 'first_name', headerName: 'Prénom', flex: 1 },
      { field: 'last_name', headerName: 'Nom', flex: 1 },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        // Bouton unique permettant à l'enseignant de retirer un badge à un étudiant
        renderCell: (params) => (
          <Button
            variant="contained"
            size="small"
            onClick={() => this.handleBadgeManagement(params.row)}
          >
            Retirer badge
          </Button>
        ),
      },
    ];
  }

  render() {
    const { role, rows } = this.props;
    const isTeacher = role === RoleIds.Teacher;

    let filteredRows = [...(rows ?? [])];

    // filtre enseignant : n'affiche que les étudiants pour un utilisateur de rôle enseignant
    if (isTeacher) {
      filteredRows = filteredRows.filter(
        (user) => user.role_id === RoleIds.Student,
      );
    }

    // tri alphabétique : d'abord par nom de famille, puis par prénom en cas d'égalité
    filteredRows.sort((a, b) => {
      const nomA = (a.last_name || '').toLowerCase();
      const nomB = (b.last_name || '').toLowerCase();

      if (nomA < nomB) return -1;
      if (nomA > nomB) return 1;

      const prenomA = (a.first_name || '').toLowerCase();
      const prenomB = (b.first_name || '').toLowerCase();

      return prenomA.localeCompare(prenomB);
    });

    // options autocomplete : génération des suggestions à partir des noms complets des utilisateurs
    const options = filteredRows.map(
      (user) => `${user.first_name} ${user.last_name}`,
    );

    // filtre recherche : réduit la liste selon la saisie de l'utilisateur
    let displayedRows = filteredRows;

    if (this.state.inputValue) {
      const search = this.state.inputValue.toLowerCase();

      displayedRows = filteredRows.filter((user) =>
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(search),
      );
    }

    // Sélection des colonnes adaptées au rôle de l'utilisateur connecté
    const columns = isTeacher
      ? this.getTeacherColumns()
      : this.getAdminColumns();

    return (
      <div style={{ width: '100%' }}>
        {/* BARRE DE RECHERCHE */}
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            freeSolo
            options={options}
            inputValue={this.state.inputValue}
            onInputChange={(event, newInputValue) => {
              this.setState({ inputValue: newInputValue });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Rechercher un utilisateur" />
            )}
          />
        </Box>

        {/* Tableau principal des utilisateurs */}
        <div style={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={displayedRows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
          />
        </div>

        {/* Popup de gestion des badges, accessible via la référence badgeAssignationPopup */}
        <UserBadgesPopup
          isOpen={this.state.open}
          selectedUser={this.state.selectedUser}
          handleClose={this.handleClose}
          onlyRemove={isTeacher}
          ref={this.badgeAssignationPopup}
        />

        {/* Popup de changement de mot de passe, disponible uniquement pour les administrateurs */}
        {this.state.selectedUser && !isTeacher && (
          <ChangePasswordPopup
            isOpen={this.state.openPasswordPopup}
            handleClose={() => this.setState({ openPasswordPopup: false })}
            userId={this.state.selectedUser.id}
          />
        )}

        {/* Boîte de dialogue de confirmation avant la suppression définitive d'un utilisateur */}
        <Dialog
          open={this.state.confirmDelete}
          onClose={() => this.setState({ confirmDelete: false })}
        >
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            Êtes-vous sûr de vouloir supprimer{' '}
            {this.state.selectedUser?.first_name}{' '}
            {this.state.selectedUser?.last_name} ?
          </DialogContent>
          <DialogActions>
            <Button onClick={this.deleteUser} color="error" variant="contained">
              Confirmer
            </Button>
            <Button
              onClick={() => this.setState({ confirmDelete: false })}
              variant="outlined"
            >
              Annuler
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default UserGrid;
